import { css } from '@emotion/core';
import { Button, message, Modal, Select, Tag } from 'antd';
import classNames from 'classnames';
import copy from 'copy-to-clipboard';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  Content,
  ContentItem,
  ContentTitle,
  FormItem,
  Icon,
  Tooltip,
} from '@/components';
import api from '../../apis';
import {
  PROJECT_PERMISSION,
  PROJECT_STATUS,
  TEAM_PERMISSION,
} from '@/constants';
import { FC, Project } from '@/interfaces';
import { AppState } from '@/store';
import {
  deleteProject,
  editProject,
  setCurrentProject,
} from '@/store/project/slice';
import style from '../../style';
import { toLowerCamelCase } from '@/utils';
import { can } from '@/utils/user';
import { getProjectModeColor, getProjectModeLabel } from '@/utils/project';
import { AuthorLabelSetting } from './AuthorLabelSetting';
import { ProjectEditForm } from './ProjectEditForm';

/** 项目基础设置的属性接口 */
interface ProjectSettingBaseProps {
  className?: string;
}
/**
 * 项目基础设置
 */
export const ProjectSettingBase: FC<ProjectSettingBaseProps> = ({
  className,
}) => {
  const history = useHistory(); // 路由
  const { formatMessage } = useIntl(); // i18n
  const dispatch = useDispatch();
  const [leaveLoading, setLeaveLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [adminJoining, setAdminJoining] = useState(false);
  const currentTeam = useSelector((state: AppState) => state.team.currentTeam);
  const currentProjectSet = useSelector(
    (state: AppState) => state.projectSet.currentProjectSet,
  );
  const currentProject = useSelector(
    (state: AppState) => state.project.currentProject,
  ) as Project;
  const userID = useSelector((state: AppState) => state.user.id);
  const [permissionsVisible, setPermissionsVisible] = useState(false);
  const [projectSetOptions, setProjectSetOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [projectSetsLoading, setProjectSetsLoading] = useState(false);

  // 拉取团队下的所有项目集，用于项目集下拉
  useEffect(() => {
    if (!currentTeam?.id) {
      return;
    }
    setProjectSetsLoading(true);
    api
      .getTeamProjectSets({
        teamID: currentTeam.id,
        params: { limit: 100 },
      })
      .then((result) => {
        const options = (result.data as any[]).map((item) => ({
          label: item.default
            ? formatMessage({ id: 'projectSet.default' })
            : item.name,
          value: item.id,
        }));
        setProjectSetOptions(options);
      })
      .catch((error) => {
        error.default();
      })
      .finally(() => {
        setProjectSetsLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTeam?.id]);

  /** 移动项目到另一个项目集 */
  const moveProjectToProjectSet = (projectSetID: string) => {
    const target = projectSetOptions.find((o) => o.value === projectSetID);
    Modal.confirm({
      title: formatMessage({
        id: 'project.moveToProjectSet',
        defaultMessage: '移动项目集',
      }),
      content: formatMessage(
        {
          id: 'project.moveToProjectSetTip',
          defaultMessage:
            '确定要把项目『{project}』移动到项目集『{projectSet}』吗？',
        },
        { project: currentProject.name, projectSet: target?.label ?? '' },
      ),
      okText: formatMessage({ id: 'form.ok' }),
      cancelText: formatMessage({ id: 'form.cancel' }),
      onOk: () => {
        return api
          .editProject({ id: currentProject.id, data: { projectSet: projectSetID } })
          .then((result) => {
            const data = toLowerCamelCase(result.data);
            dispatch(editProject(data.project));
            dispatch(setCurrentProject(data.project));
            message.success(result.data.message);
          })
          .catch((error) => {
            error.default();
          });
      },
    });
  };

  /** 删除项目：沿用原完结逻辑（清空文件与导出后进入已完结，数据不可恢复） */
  const finishProject = () => {
    setDeleteLoading(true);
    api
      .finishProject({ id: currentProject.id })
      .then((result) => {
        setDeleteLoading(false);
        const finishedProject = {
          ...currentProject,
          status: PROJECT_STATUS.FINISHED,
        };
        dispatch(deleteProject(finishedProject));
        dispatch(setCurrentProject(finishedProject));
        // 弹出提示
        message.success(result.data.message);
      })
      .catch((error) => {
        error.default();
        setDeleteLoading(false);
      });
  };

  /** 是否已归档 */
  const archived =
    currentProject.status === PROJECT_STATUS.ARCHIVED ||
    !!currentProject.archived;

  /** 归档 / 取消归档（保留数据，仅置只读并进入已完结） */
  const toggleArchive = () => {
    setArchiveLoading(true);
    const req = archived
      ? api.unarchiveProject({ id: currentProject.id })
      : api.archiveProject({ id: currentProject.id });
    req
      .then((result) => {
        setArchiveLoading(false);
        const data = toLowerCamelCase(result.data);
        const nextProject = {
          ...currentProject,
          ...(data.project ?? {}),
          status: archived ? PROJECT_STATUS.WORKING : PROJECT_STATUS.ARCHIVED,
          archived: !archived,
        };
        dispatch(editProject(nextProject));
        dispatch(setCurrentProject(nextProject));
        if (!archived) {
          // 归档后从「进行中」列表移除
          dispatch(deleteProject(nextProject));
        }
        message.success(result.data.message);
      })
      .catch((error) => {
        error.default();
        setArchiveLoading(false);
      });
  };

  /** 退出项目确认 */
  const showLeaveConfirm = () => {
    Modal.confirm({
      title: formatMessage({ id: 'project.leave' }),
      content: formatMessage({ id: 'project.leaveConfirm' }),
      onOk: () => {
        setLeaveLoading(true);
        api
          .deleteMember({
            groupType: 'project',
            groupID: currentProject.id,
            userID,
          })
          .then((result) => {
            setLeaveLoading(false);
            const data = toLowerCamelCase(result.data);
            dispatch(editProject(data.group));
            // 弹出提示
            message.success(result.data.message);
            if (currentTeam && currentProjectSet) {
              if (
                !can(currentTeam, TEAM_PERMISSION.AUTO_BECOME_PROJECT_ADMIN)
              ) {
                history.replace(
                  `/dashboard/teams/${currentTeam.id}/project-sets/${currentProjectSet.id}`,
                );
              } else {
                dispatch(setCurrentProject(data.group));
              }
            } else {
              dispatch(deleteProject({ id: data.group.id }));
              history.replace(`/dashboard/projects`);
            }
          })
          .catch((error) => {
            setLeaveLoading(false);
            error.default();
          });
      },
      onCancel: () => {},
      okText: formatMessage({ id: 'form.ok' }),
      cancelText: formatMessage({ id: 'form.cancel' }),
    });
  };

  /** 删除项目确认 */
  const confirmFinishProject = () => {
    Modal.confirm({
      title: (
        <div>
          {formatMessage(
            { id: 'project.deleteProjectTipTitle' },
            { project: currentProject.name },
          )}
        </div>
      ),
      content: formatMessage({ id: 'project.deleteProjectTip' }),
      okText: formatMessage({ id: 'project.deleteProject' }),
      okButtonProps: { danger: true },
      cancelText: formatMessage({ id: 'form.cancel' }),
      onOk() {
        finishProject();
      },
      onCancel() {},
    });
  };

  /** 归档 / 取消归档确认 */
  const confirmToggleArchive = () => {
    Modal.confirm({
      title: formatMessage({
        id: archived ? 'project.unarchiveTipTitle' : 'project.archiveTipTitle',
      }),
      content: formatMessage({
        id: archived ? 'project.unarchiveTip' : 'project.archiveTip',
      }),
      okText: formatMessage({
        id: archived ? 'project.unarchive' : 'project.archive',
      }),
      cancelText: formatMessage({ id: 'form.cancel' }),
      onOk() {
        toggleArchive();
      },
      onCancel() {},
    });
  };

  /** 团队管理员直接加入项目成为项目管理员 */
  const adminNewProject = () => {
    setAdminJoining(true);
    api
      .createApplication({
        groupType: 'project',
        groupID: currentProject.id,
        data: {
          message: '',
        },
      })
      .then((result) => {
        const data = toLowerCamelCase(result.data);
        // 加入成功
        dispatch(editProject(data.group));
        dispatch(setCurrentProject(data.group));
        // 弹出提示
        message.success(data.message);
      })
      .catch((error) => {
        error.default();
      })
      .finally(() => {
        setAdminJoining(false);
      });
  };

  return (
    <div
      className={classNames('ProjectSettingBase', className)}
      css={css`
        width: 100%;
        max-width: ${style.contentMaxWidth}px;
        padding: ${style.paddingBase}px;
        .ProjectSettingBase__PermissionsToggleIcon {
          margin-left: 5px;
        }
        .ProjectSettingBase__Danger {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
        }
        .ProjectSettingBase__DangerText {
          display: flex;
          flex-direction: column;
          gap: 2px;
          flex: auto;
          min-width: 0;
        }
        .ProjectSettingBase__DangerDesc {
          font-size: 12px;
          color: ${style.textColorSecondary};
        }
        .ProjectSettingBase__DangerTitle--danger {
          color: ${style.errorColor};
        }
      `}
    >
      <Content>
        <ContentTitle>{formatMessage({ id: 'project.me' })}</ContentTitle>
        <ContentItem>
          {formatMessage(
            { id: 'site.myRoleIs' },
            { role: currentProject.role.name },
          )}
          {currentProject.autoBecomeProjectAdmin &&
            formatMessage({ id: 'project.autoBecomeProjectAdmin' })}
          <Button
            className="ProjectSettingBase__PermissionsToggle"
            type="link"
            onClick={() => {
              setPermissionsVisible((x) => !x); // 显示/隐藏权限
            }}
          >
            {formatMessage({ id: 'site.permission' })}{' '}
            <Icon
              icon={permissionsVisible ? 'caret-up' : 'caret-down'}
              className="ProjectSettingBase__PermissionsToggleIcon"
            />
          </Button>
        </ContentItem>
        {permissionsVisible && (
          <ContentItem>
            <div className="permissions">
              {currentProject.role.permissions.map((x) => {
                return (
                  <Tooltip
                    overlayClassName="permission-tag-tooltip"
                    title={x.intro}
                    key={x.id}
                  >
                    <Tag>{x.name}</Tag>
                  </Tooltip>
                );
              })}
            </div>
          </ContentItem>
        )}
        {currentProject.autoBecomeProjectAdmin && (
          <ContentItem>
            <Button block onClick={adminNewProject} loading={adminJoining}>
              {formatMessage({ id: 'project.join' })}
            </Button>
          </ContentItem>
        )}
        {currentProject.role.systemCode !== 'creator' &&
          !currentProject.autoBecomeProjectAdmin && (
            <ContentItem>
              <Button block onClick={showLeaveConfirm} loading={leaveLoading}>
                {formatMessage({ id: 'project.leave' })}
              </Button>
            </ContentItem>
          )}
      </Content>
      <Content>
        <ContentTitle>{formatMessage({ id: 'project.info' })}</ContentTitle>
        <FormItem label={formatMessage({ id: 'project.projectType' })}>
          <Tag
            className="ProjectSettingBase__ModeTag"
            color={getProjectModeColor(
              currentProject.withProofread !== false,
              currentProject.withAuthor === true,
            )}
            style={{ marginInlineEnd: 0 }}
          >
            {getProjectModeLabel(
              formatMessage,
              currentProject.withProofread !== false,
              currentProject.withAuthor === true,
            )}
          </Tag>
        </FormItem>
        <FormItem label={formatMessage({ id: 'project.id' })}>
          {currentProject.id}{' '}
          <Button
            type="ghost"
            onClick={() => {
              const root =
                window.location.protocol + '//' + window.location.host;
              const url = root + `/dashboard/join/project/${currentProject.id}`;
              copy(url);
            }}
          >
            {formatMessage({ id: 'group.copyJoinLink' })}
          </Button>
        </FormItem>
        {!archived && (
          <ContentItem>
            <ProjectEditForm />
          </ContentItem>
        )}
        {!archived && (
          <ContentItem>
            <FormItem
              label={formatMessage({
                id: 'project.projectSet',
                defaultMessage: '所属项目集',
              })}
            >
              <Select
                style={{ width: '100%' }}
                value={currentProject.projectSet?.id}
                loading={projectSetsLoading}
                onChange={(value) => moveProjectToProjectSet(value)}
                options={projectSetOptions}
                disabled={
                  !['admin', 'creator'].includes(
                    currentProject.role?.systemCode ?? '',
                  )
                }
                placeholder={formatMessage({
                  id: 'project.projectSet',
                  defaultMessage: '所属项目集',
                })}
              />
            </FormItem>
          </ContentItem>
        )}
      </Content>
      {currentProject.withAuthor && !archived && (
        <Content>
          <ContentTitle>{formatMessage({ id: 'project.authorInfo' })}</ContentTitle>
          <ContentItem>
            <AuthorLabelSetting />
          </ContentItem>
        </Content>
      )}
      {/* <Content>
        <ContentTitle>{formatMessage({ id: 'site.aboutQuota' })}</ContentTitle>
        <ContentItem>
          {formatMessage({ id: 'site.userCount' })+ formatMessage({ id: ':' })}
          {currentProject.userCount}/{currentProject.maxUser}
        </ContentItem>
      </Content> */}
      <Content>
        {(can(currentProject, PROJECT_PERMISSION.FINISH) ||
          can(currentProject, PROJECT_PERMISSION.DELETE)) && (
          <ContentTitle>
            {formatMessage({ id: 'site.dangerZone' })}
          </ContentTitle>
        )}
        {can(currentProject, PROJECT_PERMISSION.FINISH) && (
          <ContentItem>
            <div className="ProjectSettingBase__Danger">
              <div className="ProjectSettingBase__DangerText">
                <div>
                  {formatMessage({
                    id: archived ? 'project.unarchive' : 'project.archive',
                  })}
                </div>
                <div className="ProjectSettingBase__DangerDesc">
                  {formatMessage({
                    id: archived
                      ? 'project.archiveTipArchived'
                      : 'project.archiveTip',
                  })}
                </div>
              </div>
              <Button onClick={confirmToggleArchive} loading={archiveLoading}>
                {formatMessage({
                  id: archived ? 'project.unarchive' : 'project.archive',
                })}
              </Button>
            </div>
          </ContentItem>
        )}
        {!archived && can(currentProject, PROJECT_PERMISSION.FINISH) && (
          <ContentItem>
            <div className="ProjectSettingBase__Danger">
              <div className="ProjectSettingBase__DangerText">
                <div className="ProjectSettingBase__DangerTitle--danger">
                  {formatMessage({ id: 'project.deleteProject' })}
                </div>
                <div className="ProjectSettingBase__DangerDesc">
                  {formatMessage({ id: 'project.deleteProjectTip' })}
                </div>
              </div>
              <Button
                danger
                onClick={confirmFinishProject}
                loading={deleteLoading}
              >
                {formatMessage({ id: 'project.deleteProject' })}
              </Button>
            </div>
          </ContentItem>
        )}
      </Content>
    </div>
  );
};
